import { Action, ActionNavContext, parseTokens } from '@coast/schemas';
import { mapValues } from 'lodash';
import { ActionSheetOption } from '@shared/components/ActionSheet';
import { FormContext } from '@shared/components/blocks/contexts/form';
import { ToastAPI } from '@shared/components/toast';
import { filterBySlug, resolveTemplate } from '@shared/scenes/cards/hooks';
import { CardTemplate } from '@shared/scenes/cards/types';
import { getStore } from '@shared/store';
import { API } from '@shared/types/api';
import { MBLinking } from '@shared/util/linking';
import { Navigator } from '@shared/util/navigation';
import { MBSentry } from '@shared/util/sentry';

export type ShowActionSheetFn = (options: ActionSheetOption[], title?: string) => void;

function parseFieldOverrideTokens(fieldOverrides: { [key: string]: any }) {
  const store = getStore();
  return mapValues(fieldOverrides, value =>
    parseTokens(value, { resolveCurrentUserId: () => store.getState().appState.currentUserId }),
  );
}

function navigate(
  cardId: string,
  channelSlug: string,
  context: ActionNavContext['context'],
  navigator: Navigator,
  workflowTemplateId?: string,
  templateSlug?: string,
  fieldOverrides?: { [key: string]: any },
): void {
  switch (context) {
    case 'create':
      navigator.navigate('CardCreate', { channelSlug, templateSlug, fieldOverrides });
      break;
    case 'edit':
      navigator.navigate('CardUpdate', {
        cardId,
        channelSlug,
        templateSlug,
        fieldOverrides,
        workflowTemplateId,
      });
      break;
    case 'list':
    case 'agenda':
    case 'kanban':
      navigator.navigate('Channel', { channelSlug });
      navigator.navigate('ChannelCardList', { channelSlug });
      break;
    case 'detail':
      navigator.navigate('CardDetails', { cardId, channelSlug, templateSlug });
      break;
    default:
      MBSentry.breadcrumb({
        category: 'cards',
        message: 'invalid context while evaluating navigate action',
        data: {
          cardId,
          channelSlug,
          context,
          templateSlug,
        },
      });
      break;
  }
}

async function executeAction(
  action: Action,
  card: API.CardFragment,
  channelSlug: string,
  formContext: FormContext | null,
  toast: ToastAPI,
  templates: CardTemplate[],
  navigator: Navigator,
  showActionSheet: ShowActionSheetFn,
) {
  const store = getStore();
  switch (action.type) {
    case 'actionUrl':
      if (!!action.name) {
        // @ts-expect-error -- strict migration
        MBLinking.open(formContext.values[action.name]);
      } else {
        // @ts-expect-error -- strict migration
        MBLinking.open(action.url);
      }
      break;
    case 'actionSheet':
      showActionSheet(
        // @ts-expect-error -- strict migration
        action.options.map(option => ({
          title: option.label,
          onPress: () =>
            executeActions(
              option.actions,
              card,
              channelSlug,
              formContext,
              toast,
              templates,
              navigator,
              showActionSheet,
            ),
        })),
      );
      break;
    case 'actionSetValues':
      // @ts-expect-error -- strict migration
      const prevValues = { ...formContext.values };
      action.values.forEach(({ name, value }) => {
        const parsedValue = parseTokens(value, {
          resolveCurrentUserId: () => store.getState().appState.currentUserId,
          resolveCard: () => prevValues,
        });
        // @ts-expect-error -- strict migration
        formContext.setFieldValue(name, parsedValue);
      });
      break;
    case 'actionSubmitForm':
      // @ts-expect-error -- strict migration
      await formContext.submitForm();
      break;
    case 'actionNavContext': {
      const fieldOverrides = !!action.fieldOverrides
        ? parseFieldOverrideTokens(action.fieldOverrides)
        : undefined;
      navigate(
        card.id,
        channelSlug,
        action.context,
        navigator,
        undefined,
        undefined,
        fieldOverrides,
      );
      break;
    }
    case 'actionNavTemplate': {
      let template = resolveTemplate(templates, [filterBySlug(action.templateSlug)]);
      if (!!template && template.context !== 'persistent-menu') {
        const fieldOverrides = !!action.fieldOverrides
          ? parseFieldOverrideTokens(action.fieldOverrides)
          : undefined;
        navigate(
          card.id,
          channelSlug,
          template.context as ActionNavContext['context'],
          navigator,
          undefined,
          action.templateSlug,
          fieldOverrides,
        );
      }
      break;
    }
    case 'actionToast':
      toast.show({
        // @ts-expect-error -- strict migration
        message: action.toastOptions.message,
        // @ts-expect-error -- strict migration
        style: action.toastOptions.style,
        // @ts-expect-error -- strict migration
        icon: action.toastOptions.icon,
        // @ts-expect-error -- strict migration
        actions: action.toastOptions.buttons?.map(button => ({
          label: button.label,
          onPress: () =>
            executeActions(
              // @ts-expect-error -- strict migration
              button.actions,
              card,
              channelSlug,
              formContext,
              toast,
              templates,
              navigator,
              showActionSheet,
            ),
        })),
      });
      break;
    default:
      MBSentry.breadcrumb({
        category: 'cards',
        message: 'invalid action type while executing action',
        data: {
          card,
          channelSlug,
          action,
        },
      });
      break;
  }
}

export async function executeActions(
  actions: Action[],
  card: API.CardFragment,
  channelSlug: string,
  formContext: FormContext | null,
  toast: ToastAPI,
  templates: CardTemplate[],
  navigator: Navigator,
  showActionSheet: ShowActionSheetFn,
) {
  for (let action of actions) {
    await executeAction(
      action,
      card,
      channelSlug,
      formContext,
      toast,
      templates,
      navigator,
      showActionSheet,
    );
  }
}
